import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'

interface CollapsibleSearchProps {
    value: string
    onChange: (value: string) => void
    /** Enter pressed in the field; leave out when typing already searches. */
    onSubmit?: () => void
    /** The clear (X) button, after the field has emptied itself; for a caller that keeps the search elsewhere too. */
    onClear?: () => void
    /** Names the icon button and describes the field, e.g. "Search loads". */
    label: string
    placeholder?: string
    /** `sm` is 32px (the Ongoing Loads toolbar), `md` is 36px (the height of ds2 Buttons and Selects). */
    size?: 'sm' | 'md'
}

// Whole class names, not `w-${n}`: Tailwind only emits classes it can read in the source.
const CONTROL_SIZE = { sm: { field: 'h-8', icon: 'h-8 w-8' }, md: { field: 'h-9', icon: 'h-9 w-9' } }

/**
 * Search as an icon that expands on demand and folds back when it loses focus while empty — the
 * Ongoing Loads toolbar's search, shared so other toolbars give up their always-open field's width
 * the same way. A search that is already applied stays open, otherwise the list would look unfiltered.
 */
export const CollapsibleSearch = ({ value, onChange, onSubmit, onClear, label, placeholder = label, size = 'sm' }: CollapsibleSearchProps) => {
    const [expanded, setExpanded] = useState(Boolean(value))
    const inputRef = useRef<HTMLInputElement>(null)
    const { field, icon } = CONTROL_SIZE[size]

    useEffect(() => {
        if (value) setExpanded(true)
    }, [value])

    useEffect(() => {
        if (expanded) inputRef.current?.focus()
    }, [expanded])

    if (!expanded) {
        // Padding belongs to the base classes: appending `px-0` to a string that carries `px-3.5` does not win, since Tailwind
        // orders its utilities itself, and the glyph would be squeezed to a dot.
        return (
            <button
                type="button"
                aria-label={label}
                onClick={() => setExpanded(true)}
                className={`inline-flex ${icon} shrink-0 items-center justify-center rounded-full border border-border bg-surface text-foreground hover:bg-default`}
            >
                <Search size={16} />
            </button>
        )
    }

    return (
        <div className={`flex ${field} items-center gap-1.5 rounded-full border border-border bg-surface px-3.5`}>
            <Search size={16} className="shrink-0 text-foreground-muted" />
            <input
                ref={inputRef}
                value={value}
                aria-label={label}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') onSubmit?.()
                    if (event.key === 'Escape' && !value) setExpanded(false)
                }}
                onBlur={() => {
                    if (!value) setExpanded(false)
                }}
                placeholder={placeholder}
                className="w-52 bg-transparent text-[14px] leading-[1.35] text-foreground outline-none placeholder:text-foreground-muted"
            />
            {value && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                        onChange('')
                        onClear?.()
                        setExpanded(false)
                    }}
                    className="shrink-0 text-foreground-muted hover:text-foreground"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    )
}
